/* ============================================================
   La figura: cachorro sentado de frente, carcasa de acrílico
   transparente con el pelo del perro dentro.
   Referencia: el llavero de @thepawmy — orejas de acrílico
   transparentes, el pelo solo rellena cabeza y cuerpo, y se ve
   con textura de rizo a través de la pared.
   Se dibuja por instancia (no <use>) para poder animar cada
   pieza desde CSS.
   ============================================================ */

let dogN = 0;

/* oreja izquierda; la derecha es su espejo, así siempre son simétricas */
const EAR_D = "M80 62C54 56 24 72 22 102c-1 16 4 28 12 34q7 6 14 0q7 6 14-3C60 116 58 82 80 62Z";
const MIRROR = "translate(220,0) scale(-1,1)";
const INSET_EAR = "translate(51,99) scale(.85) translate(-51,-99)";

const EARS = `
    <path d="${EAR_D}"/>
    <path d="${EAR_D}" transform="${MIRROR}"/>`;

/* cabeza, cuello, cuerpo y las dos patas delanteras en un solo trazo */
const CORE = `
    <path d="M110 48C138.7 48 162 71.3 162 100c0 18-10 33-24 42 10 8 14 24 14 44 0 12-2 22-6 28 0 14-8 23-19 23-11 0-17-8-17-20 0 12-6 20-17 20-11 0-19-9-19-23-4-6-6-16-6-28 0-20 4-36 14-44-14-9-24-24-24-42C58 71.3 81.3 48 110 48Z"/>`;

/* el pelo: mismo volumen, un poco más pequeño, orejas incluidas */
const FUR_CORE = `
    <path d="M110 51c27 0 49 22 49 49 0 17-9 31-22 40 9 8 12 25 12 41 0 11-2 20-5 26 0 13-7 21-17 21-10 0-15-8-15-19 0 11-5 19-15 19-10 0-17-8-17-21-3-6-5-15-5-26 0-16 3-33 12-41-13-9-22-23-22-40 0-27 22-49 49-49Z"/>
    <path d="${EAR_D}" transform="${INSET_EAR}"/>
    <path d="${EAR_D}" transform="${MIRROR} ${INSET_EAR}"/>`;

/* rizos: nube de bucles con jitter determinista, no un patrón repetido */
function curlSet(seed){
  let s = seed;
  const rnd = () => (s = (s * 1664525 + 1013904223) % 4294967296) / 4294967296;
  const out = [];
  const cloud = (cx, cy, rx, ry, n) => {
    for(let i = 0; i < n; i++){
      const a = rnd() * Math.PI * 2;
      const d = Math.sqrt(rnd());
      const x = cx + Math.cos(a) * rx * d;
      const y = cy + Math.sin(a) * ry * d;
      const r  = 1.7 + rnd() * 2.9;
      const a0 = rnd() * Math.PI * 2;
      const sw = 3.6 + rnd() * 2.0;
      const a1 = a0 + sw;
      out.push(`M${(x + Math.cos(a0) * r).toFixed(1)} ${(y + Math.sin(a0) * r).toFixed(1)}`
        + `A${r.toFixed(1)} ${r.toFixed(1)} 0 ${sw > Math.PI ? 1 : 0} 1 `
        + `${(x + Math.cos(a1) * r).toFixed(1)} ${(y + Math.sin(a1) * r).toFixed(1)}`);
    }
  };
  cloud(110, 100, 50, 50, 230);   // cabeza
  cloud(110, 180, 39, 36, 165);   // cuerpo
  cloud(110, 143, 30, 22, 55);    // cuello
  cloud(93, 215, 15, 20, 45);     // pata izquierda
  cloud(127, 215, 15, 20, 45);    // pata derecha
  cloud(48, 100, 20, 34, 75);     // oreja izquierda
  cloud(172, 100, 20, 34, 75);    // oreja derecha
  return out.join("");
}
/* un solo trazo con ~690 subtrazos: mismo dibujo, 700x menos nodos */
const CURLS = curlSet(20260905);

const shape = (parts, attrs = "") => `<g ${attrs}>${parts}</g>`;

function dogSVG(animated){
  const id = "d" + (++dogN);
  const cls = "dog" + (animated ? " dog--anim" : "");

  return `
<svg class="${cls}" viewBox="0 0 220 258" role="img" aria-label="">
  <defs>
    <linearGradient id="gl-${id}" x1=".1" y1="0" x2=".9" y2="1">
      <stop offset="0"   stop-color="#fff" stop-opacity=".92"/>
      <stop offset=".28" stop-color="#fff" stop-opacity=".14"/>
      <stop offset=".62" stop-color="#fff" stop-opacity=".06"/>
      <stop offset="1"   stop-color="#fff" stop-opacity=".7"/>
    </linearGradient>

    <radialGradient id="fu-${id}" cx=".35" cy=".2" r="1">
      <stop offset="0"   stop-color="var(--fur)"/>
      <stop offset=".45" stop-color="var(--fur)"/>
      <stop offset="1"   stop-color="var(--fur-dark)"/>
    </radialGradient>

    <!-- el pelo no tiene contorno limpio -->
    <filter id="sf-${id}" x="-25%" y="-25%" width="150%" height="150%">
      <feTurbulence type="fractalNoise" baseFrequency=".95" numOctaves="4" seed="7" result="n"/>
      <feDisplacementMap in="SourceGraphic" in2="n" scale="4" xChannelSelector="R" yChannelSelector="G"/>
    </filter>

    <radialGradient id="sh-${id}" cx=".5" cy=".52" r=".6">
      <stop offset=".5"  stop-color="var(--fur-dark)" stop-opacity="0"/>
      <stop offset="1"   stop-color="var(--fur-dark)" stop-opacity=".44"/>
    </radialGradient>

    <clipPath id="cp-${id}">${FUR_CORE}</clipPath>

    <!-- máscara de llenado: baja de la cabeza a las patas -->
    <mask id="mk-${id}">
      <rect class="dog__fillrect" x="-30" y="-30" width="280" height="320" fill="#fff"/>
    </mask>
  </defs>

  <!-- anilla -->
  <g class="dog__ring">
    <circle cx="110" cy="15" r="10" fill="none" stroke="#B5AA9E" stroke-width="4"/>
    <rect x="103" y="22" width="14" height="28" rx="6" fill="#CDC3B6"/>
  </g>

  <!-- pelo dentro (solo cabeza y cuerpo, como en el original) -->
  <g class="dog__fur" mask="url(#mk-${id})">
    ${shape(FUR_CORE, `fill="url(#fu-${id})" filter="url(#sf-${id})"`)}
    <g clip-path="url(#cp-${id})">
      <path d="${CURLS}" fill="none" stroke="var(--fur-dark)" stroke-opacity=".20" stroke-width="1.05"/>
      <path d="${CURLS}" fill="none" stroke="#fff" stroke-opacity=".11" stroke-width="1" transform="translate(-1.1,-1.1)"/>
      <rect x="0" y="0" width="220" height="258" fill="url(#sh-${id})"/>
    </g>
  </g>

  <!-- carcasa de acrílico -->
  <g class="dog__shell">
    ${shape(EARS + CORE, `fill="url(#gl-${id})"`)}
    ${shape(EARS + CORE, `fill="none" stroke="rgba(58,42,30,.24)" stroke-width="2.2"`)}
    ${shape(EARS, `fill="none" stroke="rgba(255,255,255,.6)" stroke-width="1.4" transform="translate(110,108) scale(.92) translate(-110,-108)"`)}
    ${shape(CORE, `fill="none" stroke="rgba(255,255,255,.5)" stroke-width="3" transform="translate(110,145) scale(.97) translate(-110,-145)"`)}
    <ellipse cx="84" cy="70" rx="19" ry="11" fill="#fff" opacity=".5" transform="rotate(-34 84 70)"/>
    <path d="M78 176c-4 18-1 34 9 45" fill="none" stroke="#fff" stroke-opacity=".38" stroke-width="6" stroke-linecap="round"/>
    <path d="M33 94c-3 12-2 23 3 32" fill="none" stroke="#fff" stroke-opacity=".5" stroke-width="5" stroke-linecap="round"/>
  </g>

  <!-- cara grabada en el acrílico -->
  <g class="dog__face" fill="rgba(44,31,22,.85)">
    <ellipse cx="90" cy="104" rx="8.2" ry="9"/>
    <ellipse cx="130" cy="104" rx="8.2" ry="9"/>
    <ellipse cx="87.2" cy="100.4" rx="2.7" ry="2.9" fill="#fff" opacity=".92"/>
    <ellipse cx="127.2" cy="100.4" rx="2.7" ry="2.9" fill="#fff" opacity=".92"/>
    <path d="M110 118c7.4 0 12 3.1 12 7.4s-5.3 8.1-12 8.1-12-3.8-12-8.1 4.6-7.4 12-7.4Z"/>
    <path d="M110 133.5c0 5.8-4.8 8.7-9.8 6.8M110 133.5c0 5.8 4.8 8.7 9.8 6.8"
          fill="none" stroke="rgba(44,31,22,.85)" stroke-width="2.6" stroke-linecap="round"/>
  </g>

  <!-- destello al cerrar -->
  <g class="dog__spark" fill="#fff">
    <path d="M58 64 l3.2 9 9 3.2 -9 3.2 -3.2 9 -3.2 -9 -9 -3.2 9 -3.2Z"/>
  </g>

  ${animated ? `
  <!-- pinzas de precisión -->
  <g class="dog__tweezers">
    <g class="dog__tuft">
      <ellipse cx="110" cy="44" rx="15" ry="10" fill="url(#fu-${id})" filter="url(#sf-${id})"/>
      <path d="M99 40c-6-3-9-8-8-13M121 41c6-4 8-9 7-14" fill="none"
            stroke="var(--fur-dark)" stroke-opacity=".65" stroke-width="2" stroke-linecap="round"/>
    </g>
    <path d="M74 -96 L101 36 L110 48 L95 34 L86 -96Z" fill="#32323A"/>
    <path d="M146 -96 L119 36 L110 48 L125 34 L134 -96Z" fill="#3E3E48"/>
    <path d="M77 -96 L86 -96 L97 22 L92 20Z" fill="#5A5A66" opacity=".75"/>
  </g>` : ``}
</svg>`;
}

function renderDogs(){
  document.querySelectorAll("[data-dog]").forEach(el => {
    if(!el.dataset.rendered){
      el.innerHTML = dogSVG(el.hasAttribute("data-dog-anim"));
      el.dataset.rendered = "1";
    }
  });
}
